import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Tag, Clock, Car } from 'lucide-react';
import type { StandardItem } from '../../types/standard';
import { WalkIcon, BookIcon, BikeIcon } from '../icons';
import { BaseDetailCard } from '../shared/BaseDetailCard';
import { useAutoTranslation } from '../../hooks/useAutoTranslation';
import { useImageDisplay } from '../../hooks/useImageDisplay';
import { isEmptyField } from '../../utils/timeUtils';
import { logger } from '../../lib/logger';
import { formatPhoneNumber } from '../../utils/formatters';
import { formatTravelTime } from '../../utils/formatDuration';
import { favoritesService } from '../../lib/favorites/favoritesService';
import { useSection } from '../../hooks/useSection';
import { analyticsService } from '../../lib/analytics/analyticsService';

interface DetailCardProps {
  item: StandardItem;
  items: StandardItem[];
  onClose: () => void;
  onItemChange: (item: StandardItem) => void;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  tableId: string;
  categoryColors: Map<string, string>;
  behavior?: 'default' | 'pin' | 'place';
  showIndex?: number | null;
  place?: any;
  onShowPlace?: (place: any) => void;
}

export const DetailCard: React.FC<DetailCardProps> = ({
  item,
  items,
  onClose,
  onItemChange,
  onCategorySelect,
  onTypeSelect,
  config,
  translations,
  tableId,
  categoryColors,
  behavior,
  showIndex,
  place,
  onShowPlace
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  const [isFavorite, setIsFavorite] = useState(() => favoritesService.isFavorite(item.id));
  
  // Use the useImageDisplay hook to determine if image should be displayed
  const { displayImage, shouldDisplay } = useImageDisplay(item.image, 'detailCard');

  // Utiliser le hook useAutoTranslation avec le sectionType
  const { translate, translating, error: translationError } = useAutoTranslation({
    description: item.description,
    currentLanguage: i18n.language,
    context: {
      tableId,
      itemId: item.id,
      sectionType: 'standard'
    }
  });

  // Effectuer la traduction quand la langue change
  useEffect(() => {
    const checkAndTranslate = async () => {
      const currentLang = i18n.language;
      
      // Vérifier si la traduction est manquante ou vide
      const currentDescription = item.descriptions[currentLang];
      const isDescriptionMissing = !currentDescription || 
                               currentDescription.trim() === '' || 
                               currentDescription === '\n';

      if (isDescriptionMissing) {
        const translated = await translate();
        if (translated) {
          // Mettre à jour les descriptions de l'item
          item.descriptions[currentLang] = translated;
          
          logger.debug('Translation completed for standard item', {
            category: 'Translation',
            data: { 
              itemId: item.id,
              itemName: item.name,
              language: currentLang,
              translatedLength: translated.length
            }
          });
        }
      }
    };

    checkAndTranslate();
  }, [item.descriptions, i18n.language, translate, item.id, item.name]);

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  const getTranslatedCategory = (category: string): string => {
    const translation = translations[category];
    if (!translation) return category;
    return translation[i18n.language] || translation.fr || category;
  };

  const renderContent = (item: StandardItem) => (
    <div className="flex flex-col gap-1 text-sm text-gray-600 mb-2">
      {/* Ligne 2: fulladress + telephone (sans icone) */}
      <div className="w-full">
        {item.fulladress && <span>{item.fulladress}</span>}
        {item.fulladress && item.telephone && <span> • </span>}
        {item.telephone && <span>{formatPhoneNumber(item.telephone)}</span>}
      </div>
      
      {/* Ligne 3: Distance + all three travel times + price + ouverture_lundi */}
      <div className="flex flex-wrap gap-2">
        {/* Distance */}
        {!isEmptyField(item.distance) && (
          <div className="flex items-center">
            <MapPin size={15} className="mr-1" />
            <span>{item.distance}km</span>
          </div>
        )}
        
        {/* Travel times */}
        {!item.inHotel && (
          <>
            {/* Walk time */}
            {!isEmptyField(item.timewalk) && (
              <div className="flex items-center">
                <WalkIcon size={15} className="mr-1" />
                <span>{formatTravelTime(item.timewalk, i18n.language)}</span>
              </div>
            )}
            
            {/* Bike time */}
            {!isEmptyField(item.timebike) && (
              <div className="flex items-center">
                <BikeIcon size={15} className="mr-1" />
                <span>{formatTravelTime(item.timebike, i18n.language)}</span>
              </div>
            )}
            
            {/* Drive time */}
            {!isEmptyField(item.timedrive) && (
              <div className="flex items-center">
                <Car size={15} className="mr-1" fill="currentColor" />
                <span>{formatTravelTime(item.timedrive, i18n.language)}</span>
              </div>
            )}
          </>
        )}
        
        {/* Prix */}
        {item.price && (
          <div 
            className="flex items-center"
            style={{ color: config['major-color'] }}
          >
            <Tag size={15} className="mr-1" />
            <span>{item.price}</span>
          </div>
        )}
        
        {/* Ouverture lundi */}
        {item.ouverture_lundi === 'oui' && (
          <div className="flex items-center">
            <Clock size={15} className="mr-1" />
            <span>{getTranslatedText('events_monday_open')}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderBookingButton = (item: StandardItem) => {
    if (!item.booking) return null;
    
    return (
      <a
        href={item.booking}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center px-4 py-2 rounded-lg text-white mb-2"
        style={{ backgroundColor: config['major-color'] }}
        onClick={() => {
          analyticsService.trackItemAction(
            section.id,
            item.id,
            'booking_click',
            'standard',
            {
              item_name: item.name,
              url: item.booking,
              section_name: section.names.fr || 'Unknown Section',
              section_type: 'standard'
            }
          );
        }}
      >
        <BookIcon size={18} className="mr-2" />
        {getTranslatedText('btn_booking')}
      </a>
    );
  };

  const handleFavoriteToggle = async (newState: boolean) => {
    try {
      if (newState) {
        favoritesService.addFavorite({
          id: item.id,
          sourceType: 'standard',
          data: item,
          metadata: {
            sourceSection: section,
            sectionSlug: section.names.fr,
            sectionNames: section.names,
            item_id: item.id,
            section_id: section.id
          }
        });
        
        analyticsService.trackItemAction(
          section.id,
          item.id,
          'add_favorite',
          'standard',
          {
            item_name: item.name,
            category: item.category,
            type: item.type,
            section_name: section.names.fr || 'Unknown Section',
            section_type: 'standard'
          }
        );
      } else {
        favoritesService.removeFavorite(item.id);
        
        analyticsService.trackItemAction(
          section.id,
          item.id,
          'remove_favorite',
          'standard',
          {
            item_name: item.name,
            category: item.category,
            type: item.type,
            section_name: section.names.fr || 'Unknown Section',
            section_type: 'standard'
          }
        );
      }
      setIsFavorite(newState);
    } catch (error) {
      logger.error('Error toggling favorite', {
        category: 'Favorites',
        data: { error, itemId: item.id }
      });
    }
  };

  // Construire l'URL d'itinéraire depuis l'hôtel
  const getHotelItineraryUrl = (item: StandardItem): string => {
    if (!item.fulladress || !config.fulladress) return '';

    // Encoder les adresses pour l'URL
    const origin = encodeURIComponent(config.fulladress);
    const destination = encodeURIComponent(item.fulladress);

    // Construire l'URL avec les coordonnées de l'hôtel comme origine
    return `https://www.google.com/maps/dir/${origin}/${destination}`;
  };

  // Obtenir la description à afficher
  const getDescription = () => {
    // Si on a une traduction valide dans les descriptions existantes
    if (item.descriptions[i18n.language]) {
      return item.descriptions[i18n.language];
    }
    
    // Fallback sur la description par défaut
    return item.description;
  };

  return (
    <BaseDetailCard
      item={item}
      items={items}
      onClose={onClose}
      onItemChange={onItemChange}
      config={config}
      translations={translations}
      tableId={tableId}
      renderContent={renderContent}
      renderBookingButton={renderBookingButton}
      onCategorySelect={onCategorySelect}
      onTypeSelect={onTypeSelect}
      categoryColors={categoryColors}
      getTranslatedCategory={getTranslatedCategory}
      getTranslatedText={getTranslatedText}
      itineraryUrl={getHotelItineraryUrl(item)}
      description={getDescription()}
      translating={translating}
      translationError={translationError}
      context={{
        sectionType: 'standard',
        itemId: item.id,
        section_id: section.id
      }}
      behavior={behavior}
      showIndex={showIndex}
      place={place}
      onShowPlace={onShowPlace}
      isFavorite={isFavorite}
      onFavoriteToggle={handleFavoriteToggle}
      image={shouldDisplay ? displayImage : undefined}
    />
  );
};

export default DetailCard;